import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React, { useState } from "react";
import "./default-table.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { EmptyTablePlaceholder } from "./EmptyTablePlaceholder";
import { useDeepCompareEffect } from "../../hooks/use-deep-compare-effect";
import { faAngleDown, faAngleUp } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";

type Props<T> = {
  data: T[];
  columnsDefinition: ColumnDef<T>[];
  onRowSelectionChange?: (
    selectedItems: Row<T>[],
    updateSelectionState?: React.SetStateAction<any>,
    resetAll?: () => void,
    allItems?: Row<T>[],
  ) => any;
};

export function Table<T>({
  data,
  columnsDefinition,
  onRowSelectionChange,
}: Props<T>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns: columnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: true,
    state: {
      sorting,
      rowSelection,
    },

    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
  });

  useDeepCompareEffect(() => {
    if (onRowSelectionChange != null) {
      onRowSelectionChange(
        table.getSelectedRowModel().flatRows,
        setRowSelection,
        table.resetRowSelection,
        table.getRowModel().flatRows,
      );
    }
  }, [rowSelection, table]);

  return (
    <>
      {data?.length === 0 ? (
        <EmptyTablePlaceholder />
      ) : (
        <div
          className={cn("overflow-x-auto")}
          style={{ maxWidth: "100%", overflowX: "auto" }}
        >
          <table className="default-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  style={{ borderTop: "none" }}
                  className={"mb-2 dark:border-gray-200"}
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={
                        "cursor-pointer table-header dark:text-gray-100"
                      }
                      style={{ width: header.getSize() }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {header.column.getIsSorted() != null &&
                        header.column.getCanSort() && (
                          <span className={"cursor-pointer ml-2"}>
                            {{
                              asc: <FontAwesomeIcon icon={faAngleUp} />,
                              desc: <FontAwesomeIcon icon={faAngleDown} />,
                            }[header.column.getIsSorted() as string] ?? null}
                          </span>
                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className={"mt-2"}>
              <tr style={{ height: 15 }} />
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  role={"row"}
                  className={
                    "hover:bg-gray-100 dark:hover:bg-neutral-600 border-#EBEDF3 border-t dark:border-t-gray-500"
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={"py-3"}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
}

export const MemoizedTable = React.memo(Table);
